import React from "react"
import { Form, Formik } from "formik"
import { useSelector } from "react-redux"
import { FormControl, FormHelperText, InputAdornment, InputLabel, ListSubheader, MenuItem, Select, Stack, TextField } from "@mui/material"

import ModalForm from "../../../../../components/form/ModalForm"
import { getPackagingResumeFormInitialValues, packagingNature } from "../../../../../utils/packaging"
import { packagingResumeSchema } from "../../../../../utils/yupValidators"
import { getPackagingCategoriesSelector, getPackagingTypesSelector } from "../../../../../reducers/Packaging/packagingList"
import { getBrandsOptions } from "../../../../../utils"
import ModalActions from "../../../../../components/ModalActions"
import { getOrganizationsEnterprisesSelector } from "../../../../../reducers/Organizations/organizations"

const sx = {
  formControl: {
    "& .MuiFormLabel-root": {
      position: "relative",
      display: "flex",
      alignItems: "center",
      transform: "none",
      fontSize: 14,
    },
    "& .MuiInputBase-root": {
      marginTop: 0
    }
  }
}

const PackagingResumeFormDialog = ({
  onClose,
  open,
  onSave,
  packaging
}) => {
  const types = useSelector(getPackagingTypesSelector)
  const categories = useSelector(getPackagingCategoriesSelector)
  const activeEnterprises = useSelector(getOrganizationsEnterprisesSelector).filter(e => e.status !== "ARCHIVED")

  return (
    <ModalForm
      onClose={onClose}
      open={open}
      title="Modifier le résumé"
      sx={{ pt: 2 }}
    >
      <Formik
        initialValues={getPackagingResumeFormInitialValues(packaging)}
        onSubmit={onSave}
        validationSchema={packagingResumeSchema}
        validateOnBlur={false}
        validateOnChange={false}
      >
        {({ values, handleChange, errors, setFieldValue }) => {
          const combinedValue = [
            ...(values.brands || []).map(b => `b:${b}`),
            ...(values.enterprises || []).map(e => `e:${e}`),
          ]

          const handleCombinedChange = (event) => {
            const selected = event.target.value
            setFieldValue("brands", selected.filter(v => v.startsWith("b:")).map(v => v.slice(2)))
            setFieldValue("enterprises", selected.filter(v => v.startsWith("e:")).map(v => v.slice(2)))
          }

          return (
            <Form>
              <Stack spacing={3}>
                <FormControl margin="normal" fullWidth sx={sx.formControl}>
                  <TextField
                    variant="standard"
                    name="name"
                    label="Nom"
                    value={values.name}
                    error={!!errors.name}
                    helperText={errors.name}
                    onChange={handleChange}
                  />
                  {errors.name && <FormHelperText error>{errors.name}</FormHelperText>}
                </FormControl>

                <FormControl margin="normal" fullWidth sx={sx.formControl}>
                  <TextField
                    variant="standard"
                    name="reference"
                    label="Référence"
                    value={values.reference}
                    error={!!errors.reference}
                    helperText={errors.reference}
                    onChange={handleChange}
                  />
                  {errors.reference && <FormHelperText error>{errors.reference}</FormHelperText>}
                </FormControl>

                <FormControl fullWidth error={!!errors.type} sx={sx.formControl}>
                  <InputLabel id="type">Type</InputLabel>
                  <Select
                    labelId="type"
                    variant="standard"
                    name="type"
                    label="Type"
                    value={values.type}
                    onChange={handleChange}
                  >
                    {types.map(type => (
                      <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                    ))}
                  </Select>
                  {errors.type && <FormHelperText error>{errors.type}</FormHelperText>}
                </FormControl>

                <FormControl margin="normal" fullWidth error={!!errors.nature} sx={sx.formControl}>
                  <InputLabel id="nature">Nature du packaging</InputLabel>
                  <Select
                    labelId="nature"
                    variant="standard"
                    name="nature"
                    label="Nature du packaging"
                    value={values.nature}
                    onChange={handleChange}
                  >
                    {packagingNature.map(type => (
                      <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                    ))}
                  </Select>
                  {errors.nature && <FormHelperText error>{errors.nature}</FormHelperText>}
                </FormControl>

                <FormControl margin="normal" fullWidth error={!!errors.categories} sx={sx.formControl}>
                  <InputLabel id="categories">Catégorie</InputLabel>
                  <Select
                    labelId="categories"
                    variant="standard"
                    name="categories"
                    label="Catégorie"
                    value={values.categories}
                    onChange={handleChange}
                  >
                    {categories.map(type => (
                      <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                    ))}
                  </Select>
                  {errors.categories && <FormHelperText error>{errors.categories}</FormHelperText>}
                </FormControl>

                <FormControl margin="normal" fullWidth error={!!errors.brands} sx={sx.formControl}>
                  <InputLabel id="brands-enterprises">Marques / Entreprises</InputLabel>
                  <Select
                    labelId="brands-enterprises"
                    variant="standard"
                    label="Marques / Entreprises"
                    value={combinedValue}
                    onChange={handleCombinedChange}
                    multiple
                  >
                    <ListSubheader>Marques</ListSubheader>
                    {getBrandsOptions().map(brand => (
                      <MenuItem key={`b:${brand.value}`} value={`b:${brand.value}`}>{brand.label}</MenuItem>
                    ))}
                    <ListSubheader>Entreprises</ListSubheader>
                    {activeEnterprises.map(e => (
                      <MenuItem key={`e:${e.objectId}`} value={`e:${e.objectId}`}>{e.name}</MenuItem>
                    ))}
                  </Select>
                  {errors.brands && <FormHelperText error>{errors.brands}</FormHelperText>}
                </FormControl>

                <FormControl margin="normal" fullWidth sx={sx.formControl}>
                  <TextField
                    variant="standard"
                    name="price"
                    label="Prix"
                    value={values.price}
                    type="number"
                    error={!!errors.price}
                    helperText={errors.price}
                    disabled
                    InputProps={{
                      type: "number",
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                      endAdornment: (
                        <InputAdornment position="end">€</InputAdornment>
                      ),
                      disableUnderline: false,
                    }}
                  />
                </FormControl>
              </Stack>
              <ModalActions onCancel={onClose} />
            </Form>
          )
        }}
      </Formik>
    </ModalForm>
  )
}

export default PackagingResumeFormDialog
